import React, { useEffect, useState } from "react";
import { XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, ReferenceLine, AreaChart, Area, Brush } from "recharts";
import { Select, Modal, Spin } from "antd";
import { ExpandOutlined } from '@ant-design/icons';
import { AiOutlineSound } from "react-icons/ai";
import dayjs from 'dayjs';
import '../styles/Audio.css';
import VibrazioneIcon from "../assets/icons/Vibrazione.svg"
import { IoEarth } from "react-icons/io5";
import { fetchNoiseData, fetchDetectedSounds, fetchNoiseDailyData, fetchNoiseIndex } from '../APIs/apiService';

// Helper function to process detected sounds data for a specific date
const processDetectedSounds = (detectedSoundsData, selectedDate) => {
  if (!detectedSoundsData || !selectedDate) return [];

  // Convert selected date to timestamp range (start and end of day)
  const dateObj = new Date(selectedDate);
  const startOfDay = new Date(dateObj.setHours(0, 0, 0, 0)).getTime() / 1000; // Unix timestamp in seconds
  const endOfDay = new Date(dateObj.setHours(23, 59, 59, 999)).getTime() / 1000;

  // Create a timeline structure for the entire day (24 hours * 60 minutes = 1440 minutes)
  const minutesInDay = 24 * 60;
  const timeline = Array(minutesInDay).fill(null).map(() => ({}));

  // Process each sound type
  Object.entries(detectedSoundsData).forEach(([soundType, detections]) => {
    detections.forEach(detection => {
      const { start, end, confidence } = detection;

      // Check if detection is within selected date
      if (start >= startOfDay && start <= endOfDay) {
        // Calculate which minute slots this detection covers
        const startMinute = Math.floor((start - startOfDay) / 60);
        const endMinute = Math.floor((end - startOfDay) / 60);

        // Mark the timeline slots
        for (let minute = startMinute; minute <= Math.min(endMinute, minutesInDay - 1); minute++) {
          if (!timeline[minute][soundType]) {
            timeline[minute][soundType] = 0;
          }
          // Use confidence as intensity (0-1 range, multiply by 100 for percentage)
          timeline[minute][soundType] = Math.max(timeline[minute][soundType], confidence * 100);
        }
      }
    });
  });

  // Convert timeline to the format expected by the chart
  return timeline.map((sounds, index) => {
    const hour = Math.floor(index / 60);
    const minute = index % 60;
    return {
      time: new Date(startOfDay * 1000 + index * 60 * 1000).getTime(),
      timeLabel: `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`,
      'Wind / White noise': sounds['Wind / White noise'] || 0,
      'Human voice': sounds['Human voice'] || 0,
      'Road transport': sounds['Road transport'] || 0,
      'Aircraft': sounds['Aircraft'] || 0,
      'Music': sounds['Music'] || 0,
      'Water transport': sounds['Water transport'] || 0,
      'Outside, urban or manmade': sounds['Outside, urban or manmade'] || 0
    };
  });
};

const AudioChart = () => {
  const [decibelData, setDecibelData] = useState([]);
  const [selectedDecibelDate, setSelectedDecibelDate] = useState(null);
  const [viewMode, setViewMode] = useState('overall'); // 'overall' or 'sounds'
  const [soundsData, setSoundsData] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [useNewFormat, setUseNewFormat] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [detectedSoundsData, setDetectedSoundsData] = useState(null);

  // Load detected sounds data on component mount using centralized API service
  useEffect(() => {
    fetchDetectedSounds()
      .then(data => {
        setDetectedSoundsData(data);
        console.log("Detected sounds data loaded:", Object.keys(data));
      })
      .catch(err => console.error("Error loading detected sounds:", err));
  }, []);

  useEffect(() => {
    setIsLoading(true);
    // Try new format first using centralized API service
    fetchNoiseData()
      .then(data => {
        setUseNewFormat(true);
        // Extract unique dates from new format
        const allDates = [...new Set(data.map(item => {
          const timestamp = new Date(parseInt(item.time) * 1000);
          return timestamp.toISOString().split('T')[0];
        }))].sort();

        // Filter dates to show only 10/05/2025 - 18/05/2025
        const startDate = '2025-05-10';
        const endDate = '2025-05-18';
        const dates = allDates.filter(date => date >= startDate && date <= endDate);

        setAvailableDates(dates);
        if (dates.length > 0) {
          const firstDate = dates[0]; // Set first date as default
          setSelectedDecibelDate(firstDate);
        }
        setIsLoading(false);
      })
      .catch(() => {
        // Fallback to old format
        console.warn("Failed to load Noise_1min.json, falling back to json_giorni");
        setUseNewFormat(false);
        fetchNoiseIndex()
          .then(dates => {
            if (dates && dates.length > 0) {
              setSelectedDecibelDate(dates[0]);
              setAvailableDates(dates);
            }
            setIsLoading(false);
          })
          .catch(err => {
            console.error("Errore nel caricamento delle date:", err);
            setIsLoading(false);
          });
      });
  }, []);


  useEffect(() => {
    if (!selectedDecibelDate) return;

    if (useNewFormat) {
      // Load and process new format data using centralized API service
      fetchNoiseData()
        .then((allData) => {
          // Filter data for selected date
          const filteredData = allData.filter(item => {
            const timestamp = new Date(parseInt(item.time) * 1000);
            const itemDate = timestamp.toISOString().split('T')[0];
            return itemDate === selectedDecibelDate;
          });

          // Process data at 1-minute intervals
          const minuteData = filteredData.map((item) => {
            const timestamp = new Date(parseInt(item.time) * 1000);
            const timeKey = `${timestamp.getHours().toString().padStart(2, '0')}:${timestamp.getMinutes().toString().padStart(2, '0')}`;

            const decibelValue = parseFloat(item['LZeq_dBFS']);

            return {
              time: timeKey,
              decibel: !isNaN(decibelValue) ? Math.round(decibelValue * 10) / 10 : null,
              hour: timestamp.getHours()
            };
          }).filter(item => item.decibel !== null);

          // Sort by time
          minuteData.sort((a, b) => {
            const [aHour, aMin] = a.time.split(':').map(Number);
            const [bHour, bMin] = b.time.split(':').map(Number);
            return (aHour * 60 + aMin) - (bHour * 60 + bMin);
          });

          setDecibelData(minuteData);

          // Process detected sounds data for the selected date
          if (detectedSoundsData) {
            const processedSounds = processDetectedSounds(detectedSoundsData, selectedDecibelDate);
            setSoundsData(processedSounds);
          } else {
            setSoundsData([]);
          }
        })
        .catch(err => console.error("Error loading noise data:", err));
    } else {
      // Old format processing using centralized API service
      fetchNoiseDailyData(selectedDecibelDate)
        .then((data) => {
          const giorno = data.timestamp;

          //utc +2
          const df = data.data.map((d) => {
            const datetime = new Date(`${giorno}T${d.time}`);
            datetime.setHours(datetime.getHours() + 2);
            return {
              datetime,
              decibel: parseFloat(d.db),
              sounds: d.sounds || {} // Extract sounds data if available
            };
          });

          const by5min = {};
          df.forEach((row) => {
            const rounded = new Date(Math.floor(row.datetime.getTime() / (5 * 60 * 1000)) * (5 * 60 * 1000));
            const key = rounded.toISOString();
            if (!by5min[key]) by5min[key] = [];
            by5min[key].push(row.decibel);
          });

          // Create full time range from 00:00 to 23:00 (hourly)
          const fullTimeRange = [];
          for (let hour = 0; hour < 24; hour++) {
            fullTimeRange.push(`${hour.toString().padStart(2, '0')}:00`);
          }

          // Initialize data map with all time slots
          const dataMap = new Map();
          fullTimeRange.forEach(time => {
            dataMap.set(time, {
              time,
              decibel: null,
              hour: parseInt(time.split(':')[0])
            });
          });

          // Media ogni 5 minuti e aggregate per hour
          const hourlyData = {};
          Object.entries(by5min).forEach(([time, values]) => {
            const date = new Date(time);
            const hourKey = `${date.getHours().toString().padStart(2, '0')}:00`;
            if (!hourlyData[hourKey]) hourlyData[hourKey] = [];
            hourlyData[hourKey].push(...values);
          });

          // Populate data map with hourly averages
          Object.entries(hourlyData).forEach(([hourKey, values]) => {
            if (dataMap.has(hourKey)) {
              dataMap.set(hourKey, {
                time: hourKey,
                decibel: Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 10) / 10,
                hour: parseInt(hourKey.split(':')[0])
              });
            }
          });

          setDecibelData(Array.from(dataMap.values()));

          // Process sounds data for the categorical chart
          const soundsProcessed = df.map((d) => ({
            time: d.datetime.getTime(),
            timeLabel: d.datetime.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
            vento: d.sounds.vento || 0,
            persone: d.sounds.persone || 0,
            treni: d.sounds.treni || 0,
            animali: d.sounds.animali || 0,
            autobus: d.sounds.autobus || 0,
            auto: d.sounds.auto || 0
          }));

          setSoundsData(soundsProcessed);
        });
    }
  }, [selectedDecibelDate, useNewFormat, detectedSoundsData]);


  // Render chart component
  const renderChart = (height = 200) => (
    <>
      {decibelData.length > 0 && viewMode === 'overall' && (
        <ResponsiveContainer width="100%" height={height}>
          <AreaChart data={decibelData}>
            <defs>
              <linearGradient id="colorAudio" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#FF8C42" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#FF8C42" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="time"
              tick={{ fontSize: 11, fill: '#666' }}
              axisLine={{ stroke: '#e0e0e0' }}
              interval="preserveStartEnd"
              ticks={['00:00', '04:00', '08:00', '12:00', '16:00', '20:00', '23:59']}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#666' }}
              axisLine={{ stroke: '#e0e0e0' }}
            />
            <Tooltip
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  const value = payload[0].value;
                  return (
                    <div style={{
                      background: 'white',
                      padding: '8px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}>
                      <p style={{ margin: '2px 0' }}><strong>Ora:</strong> {label}</p>
                      <p style={{ margin: '2px 0' }}><strong>Livello:</strong> {value} dB</p>
                      <p style={{ margin: '2px 0', fontSize: '11px', color: '#666' }}>
                        {value < 0 ? '🔇 Audio basso (normale)' : '🔊 Audio alto'}
                      </p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <ReferenceLine y={0} stroke="#999" strokeDasharray="5 5" />
            <Area
              type="monotone"
              dataKey="decibel"
              stroke="#FF8C42"
              strokeWidth={2}
              fill="url(#colorAudio)"
              connectNulls={true}
            />
            <Brush
              dataKey="time"
              height={30}
              stroke="#8884d8"
              fill="#f5f5f5"
              travellerWidth={10}
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </>
  );

  if (isLoading) {
    return (
      <div className="proprieta-wrapper">
        <div className="proprieta-title">
          <img src={VibrazioneIcon} alt="Congestion" className="proprieta-icon-img" />
          <h3>Rumorosita</h3>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
          <Spin size="large" tip="Caricamento dati rumorosità..." />
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="proprieta-wrapper">
      <div className="proprieta-title">
        <img src={VibrazioneIcon} alt="Congestion" className="proprieta-icon-img" />
        <h3>Rumorosita</h3>
      </div>
      <div className="proprieta-content anomaly-content">
        <p className="proprieta-description anomaly-description">
          Seleziona la data per la quale vuoi visualizzare i valori.
        </p>
        <Select
          placeholder="Seleziona data"
          className="anomaly-date-picker"
          value={selectedDecibelDate}
          onChange={(date) => setSelectedDecibelDate(date)}
          getPopupContainer={(trigger) => trigger.parentElement}
          popupStyle={{ zIndex: 9999 }}
          style={{ borderRadius: "20px" }}
        >
          {availableDates.map(date => (
            <Select.Option key={date} value={date}>
              {dayjs(date).format('DD/MM/YYYY')}
            </Select.Option>
          ))}
        </Select>
      </div>

      <div className="audio-view-toggle">
        <button
          className={`toggle-btn ${viewMode === 'overall' ? 'active' : ''}`}
          onClick={() => setViewMode('overall')}
        >
          <AiOutlineSound size={16} />
          <span>Rumore complessivo</span>
        </button>
        <button
          className={`toggle-btn ${viewMode === 'sounds' ? 'active' : ''}`}
          onClick={() => setViewMode('sounds')}
        >
          <IoEarth size={16} />
          <span>Suoni rilevati</span>
        </button>
      </div>
 
        {decibelData.length > 0 ? (
          <>
            {viewMode === 'overall' ? (
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setIsExpanded(true)}
                  style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    width: '36px',
                    height: '36px',
                    backgroundColor: 'rgba(24, 144, 255, 0.9)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                    zIndex: 100,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    transition: 'all 0.3s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(64, 169, 255, 1)';
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(24, 144, 255, 0.9)';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  title="Espandi grafico"
                >
                  <ExpandOutlined />
                </button>
                {renderChart()}
              </div>
            ) : (
              <div className="sounds-chart-container">
                <div className="sounds-chart-header">Suoni rilevati</div>
                {soundsData.length > 0 ? (
                  <>
                    <div className="sounds-chart">
                      {[
                        'Road transport',
                        'Human voice',
                        'Wind / White noise',
                        'Aircraft',
                        'Music',
                        'Water transport',
                        'Outside, urban or manmade'
                      ].map((soundType) => {
                        const hasData = soundsData.some(d => d[soundType] > 0);
                        if (!hasData) return null;

                        return (
                          <div key={soundType} className="sound-row">
                            <div className="sound-label">
                              {soundType}
                            </div>
                            <div className="sound-timeline">
                              {soundsData.map((dataPoint, idx) => {
                                const value = dataPoint[soundType];
                                if (value > 0) {
                                  return (
                                    <div
                                      key={idx}
                                      className="sound-block"
                                      style={{
                                        left: `${(idx / soundsData.length) * 100}%`,
                                        width: `${(1 / soundsData.length) * 100}%`,
                                        opacity: Math.min(value / 100, 1)
                                      }}
                                      title={`${dataPoint.timeLabel}: ${Math.round(value)}% confidence`}
                                    />
                                  );
                                }
                                return null;
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="sounds-chart-xaxis">
                      {['00:00', '04:00', '08:00', '12:00', '16:00', '20:00', '24:00'].map((label) => {
                        // Calculate position: hours go from 0 to 24, position should be (hour/24)*100%
                        const hour = parseInt(label.split(':')[0]);
                        const position = (hour / 24) * 100;
                        return (
                          <span key={label} style={{ left: `${position}%` }}>
                            {label}
                          </span>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <div className="audio-no-data">
                    <p>⚠️ Dati di classificazione dei suoni non disponibili per questa data.</p>
                    <p style={{ fontSize: '12px', marginTop: '8px', color: '#999' }}>
                      Seleziona una data per visualizzare i suoni rilevati.
                    </p>
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="audio-no-data">
            <p>Seleziona una data per visualizzare il grafico.</p>
          </div>
        )}
      </div>

    {/* Expanded Chart Modal */}
    <Modal
      open={isExpanded}
      onCancel={() => setIsExpanded(false)}
      footer={null}
      width="95%"
      centered
      title="Rumorosità - Vista Espansa"
      destroyOnClose
      zIndex={9999}
      style={{ top: 20 }}
    >
      <div style={{ padding: '20px 0' }}>
        {renderChart(500)}
      </div>
    </Modal>
    </>
  );
};

export default AudioChart;

/*
================================================================================
OTTIMIZZAZIONI COMPONENTE AUDIO - REPORT PER SUPERVISORE
================================================================================


   ✅ SOLUZIONE: Pannello informativo che spiega normalità valori negativi in audio digitale

   ✅ SOLUZIONE: Media mobile ogni 5 minuti per smoothing dati

   ✅ SOLUZIONE: Filtro fascia oraria 6:00-22:00 (basato su analisi notebook)

   ✅ SOLUZIONE: Reference line a 0 dB + tooltip educativi con icone

MIGLIORAMENTI TECNICI IMPLEMENTATI:
----------------------------------
• 🔊 Pannello esplicativo con background colorato e icone intuitive
• ⏰ Filtro intelligente fascia oraria (hour >= 6 && hour <= 22)  
• 📊 Algoritmo media 5 minuti con arrotondamento 1 decimale
• 📍 ReferenceLine React component a 0 dB (linea tratteggiata)
• 💡 Tooltip custom con logica condizionale (alto/basso volume)
• 🎨 Styling migliorato: colore arancione (#ff7300), strokeWidth=2


================================================================================
*/
